export interface ParentIds {
    show_id: string;
    show_name: string;
    collection_id: string;
    collection_name: string;
    category_id: string;
    category_name: string;
}

export const EMPTY_PARENT_PAYLOAD: ParentIds = {
    show_id: '',
    show_name: '',
    collection_id: '',
    collection_name: '',
    category_id: '',
    category_name: '',
};

const LINKED_PARENT_ENTITY_TYPES = new Set(['EPISODE', 'SHOW', 'COLLECTION']);

const entityLabel = (entity?: { id?: string; name?: string; title?: string }) =>
    entity?.name || entity?.title || '';

/** Normalize API response into stable parent id/name fields. */
export function normalizeLinkedParentsResponse(response: unknown): ParentIds {
    const payload =
        (response as { data?: Record<string, { id?: string; name?: string; title?: string }> })
            ?.data ?? (response as Record<string, { id?: string; name?: string; title?: string }>) ?? {};

    return {
        show_id: payload.show?.id ?? '',
        show_name: entityLabel(payload.show),
        collection_id: payload.collection?.id ?? '',
        collection_name: entityLabel(payload.collection),
        category_id: payload.category?.id ?? '',
        category_name: entityLabel(payload.category),
    };
}

export function toApiEntityType(redirectType: string): string {
    return redirectType.trim().toUpperCase();
}

/** Whether save should call the linked parent API for this redirect. */
export function shouldResolveLinkedParents(
    redirectType: string,
    redirectId: string
): boolean {
    const entityType = toApiEntityType(redirectType);
    if (!redirectId.trim()) return false;
    if (entityType === 'OTHER' || entityType === 'CATEGORY') return false;
    return LINKED_PARENT_ENTITY_TYPES.has(entityType);
}

/** Always include all parent keys (empty string when no value). */
export function pickParentPayloadFields(ids: ParentIds): ParentIds {
    return {
        show_id: ids.show_id || '',
        show_name: ids.show_name || '',
        collection_id: ids.collection_id || '',
        collection_name: ids.collection_name || '',
        category_id: ids.category_id || '',
        category_name: ids.category_name || '',
    };
}

/** Map resolved parents onto widget-level redirection fields. */
export function buildRedirectionParentFields(
    redirectType: string,
    redirectId: string,
    resolved: ParentIds
): ParentIds {
    const redirectionTo = toApiEntityType(redirectType);
    const redirectionId = redirectId.trim();
    const normalized = pickParentPayloadFields(resolved);

    if (redirectionTo === 'SHOW') {
        return pickParentPayloadFields({
            show_id: normalized.show_id || redirectionId,
            show_name: normalized.show_name,
            collection_id: normalized.collection_id,
            collection_name: normalized.collection_name,
            category_id: normalized.category_id,
            category_name: normalized.category_name,
        });
    }

    if (redirectionTo === 'COLLECTION') {
        return pickParentPayloadFields({
            show_id: '',
            show_name: '',
            collection_id: normalized.collection_id || redirectionId,
            collection_name: normalized.collection_name,
            category_id: normalized.category_id,
            category_name: normalized.category_name,
        });
    }

    return normalized;
}
